import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, Send } from "lucide-react";
import { Diamond } from "@/design-system/MemberRatingPill";
import { IKO } from "@/design-system/tokens";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";

/**
 * Iter 28 · Vania Msg 231 — event page composers.
 *
 * Gated to signed-in, paid, and verified-went members. Signed-in members who
 * haven't gone see a friendly "mark went to share" line. Guests see the join
 * prompt. Composers stay collapsed until the member taps "share a photo" or
 * "leave a review" — no dead cards.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });
const CARD = "var(--ww-surface)";
const LINE = "var(--ww-line)";
const META = "var(--ww-text-muted)";
const YELLOW = "#FFD60A";

function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function EventComposers({ eventId, isPaid, ownIntent, canPostAndReview, isSignedIn, eventStarted, onIWent, iWent, initialOpen, passed = false, iko }) {
  const { user } = useAuth();
  const [openPhoto, setOpenPhoto] = useState(initialOpen === "photo");
  const [openReview, setOpenReview] = useState(initialOpen === "review");
  const rootRef = useRef(null);

  // WW-169 · the "this event has passed" card opens the composers.
  useEffect(() => {
    const onOpen = (e) => {
      const c = e.detail?.compose;
      if (c === "photo") setOpenPhoto(true);
      if (c === "review") setOpenReview(true);
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    };
    window.addEventListener("ww:composer:open", onOpen);
    return () => window.removeEventListener("ww:composer:open", onOpen);
  }, []);

  // Msg 234 §4 · when we're opened by ?compose=photo (from the stories row),
  // scroll ourselves into view so the composer is immediately actionable.
  useEffect(() => {
    if (!initialOpen) return;
    const t = setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [initialOpen]);

  if (!user) return null;
  if (passed && !canPostAndReview) return null;

  if (!canPostAndReview) {
    // Msg 234 · self-declared "i went" unlocks reviews and posts for paid /
    // trial members once the event has started. Update copy accordingly.
    return (
      <div ref={rootRef} data-testid="event-composers-locked"
           style={{ marginTop: 20, background: CARD, border: `1px solid ${LINE}`,
                    borderRadius: 14, padding: 14 }}>
        <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14, textTransform: "lowercase", color: "var(--ww-text)" }}>
          been here?
        </p>
        <p style={{ fontFamily: "Arial", fontSize: 13, color: META, marginTop: 4 }}>
          {!isPaid
            ? (!!user ? "become an insider to review and share photos." : "get in to review and share photos.")
            : !eventStarted
              ? "the i went button opens once the event has started. come back after and share how it went."
              : "went? tap i went to leave a review and share photos."}
        </p>
        {isPaid && eventStarted && !iWent && (
          <button data-testid="composer-iwent" onClick={onIWent}
                  style={{ marginTop: 10, padding: "10px 18px", borderRadius: 999,
                           background: YELLOW, color: "#1A1A30", border: `1px solid ${YELLOW}`,
                           fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                           textTransform: "lowercase", cursor: "pointer", minHeight: 44 }}>
            i went
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="mt-5 flex flex-col gap-3" data-testid="event-composers">
      {!passed && <div className="flex gap-2 flex-wrap">
        <button data-testid="composer-open-photo"
                onClick={() => setOpenPhoto((v) => !v)}
                style={ChipStyle(openPhoto)}>
          <Camera size={14} /> share a photo
        </button>
        <button data-testid="composer-open-review"
                onClick={() => setOpenReview((v) => !v)}
                style={ChipStyle(openReview)}>
          <Diamond color="currentColor" size={14} filled={false} /> leave a review
        </button>
      </div>}
      {openPhoto && <PhotoComposer eventId={eventId} onDone={() => setOpenPhoto(false)} />}
      {openReview && <ReviewComposer eventId={eventId} iko={iko} onDone={() => setOpenReview(false)} />}
    </div>
  );
}

function ChipStyle(on) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "10px 14px", borderRadius: 999,
    background: on ? YELLOW : "transparent",
    color: on ? "#1A1A30" : "var(--ww-text)",
    border: `1px solid ${on ? YELLOW : LINE}`,
    fontFamily: "Poppins", fontWeight: 600, fontSize: 13,
    textTransform: "lowercase", cursor: "pointer", minHeight: 44,
  };
}

function PhotoComposer({ eventId, onDone }) {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();
  const onFiles = (e) => {
    const arr = Array.from(e.target.files || []).slice(0, 6);
    setFiles(arr);
  };
  const submit = async () => {
    if (!files.length) { toast.error("pick at least one photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption.slice(0, 300));
      files.forEach((f) => fd.append("files", f));
      await client.post(`/events/${eventId}/posts`, fd, {
        headers: { ...bearer(), "Content-Type": "multipart/form-data" },
      });
      toast.success("photo shared");
      setFiles([]); setCaption("");
      onDone && onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error || "could not share. try again."));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="composer-photo" className="rounded-2xl border p-4"
         style={{ borderColor: LINE, background: CARD }}>
      <button type="button" onClick={pick} data-testid="composer-photo-pick"
              style={{ ...ChipStyle(false), border: `1px dashed ${LINE}` }}>
        <Camera size={14} /> {files.length ? `${files.length} photo${files.length > 1 ? "s" : ""} picked` : "pick photos"}
      </button>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
             multiple onChange={onFiles} style={{ display: "none" }}
             data-testid="composer-photo-input" />
      {files.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {files.map((f, i) => (
            <img key={i} alt="" src={URL.createObjectURL(f)}
                 style={{ width: 64, height: 64, borderRadius: 8, objectFit: "cover" }} />
          ))}
        </div>
      )}
      <textarea data-testid="composer-photo-caption" placeholder="a line about the event (optional)"
                value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 300))} rows={2}
                style={inputStyle} />
      <div className="mt-2 flex justify-end">
        <button data-testid="composer-photo-submit" onClick={submit} disabled={busy}
                style={SubmitStyle(busy)}>
          <Send size={14} /> {busy ? "sharing…" : "share"}
        </button>
      </div>
    </div>
  );
}

function ReviewComposer({ eventId, iko, onDone }) {
  const diamond = IKO[iko]?.bright || IKO.sherehe.bright;
  const [score, setScore] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!score) { toast.error("pick a rating out of 5"); return; }
    setBusy(true);
    try {
      await client.post(`/events/${eventId}/reviews`, { member_score: score, note: note.slice(0, 1000) },
                         { headers: bearer() });
      toast.success("review shared");
      // Wave 16.2 (Vania 2 Oct) · review_posted analytics tap. Fire-and-forget.
      import("@/lib/analytics").then((m) => m.trackReviewPosted(eventId, score));
      setScore(0); setNote("");
      onDone && onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "could not post. try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="composer-review" className="rounded-2xl border p-4"
         style={{ borderColor: LINE, background: CARD }}>
      <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginBottom: 6 }}>
        one review per event · shown separately from the watu-wako score.
      </p>
      <div className="flex gap-1" role="radiogroup" aria-label="rating out of 5" data-testid="composer-review-stars">
        {[1,2,3,4,5].map((n) => (
          <button key={n} type="button" onClick={() => setScore(n)} aria-label={`${n} out of 5`}
                  role="radio" aria-checked={n === score}
                  data-testid={`composer-review-star-${n}`}
                  style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, minHeight: 44 }}>
            <Diamond color={diamond} size={26} filled={n <= score} />
          </button>
        ))}
      </div>
      <textarea data-testid="composer-review-note" placeholder="what stood out? (optional)"
                value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={3}
                style={inputStyle} />
      <div className="mt-2 flex justify-end">
        <button data-testid="composer-review-submit" onClick={submit} disabled={busy}
                style={SubmitStyle(busy)}>
          <Send size={14} /> {busy ? "posting…" : "post review"}
        </button>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 10,
  background: "var(--ww-sunken)", border: `1px solid ${LINE}`,
  color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14,
  outline: "none", marginTop: 10, resize: "vertical",
};

function SubmitStyle(busy) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "10px 18px", borderRadius: 999, background: YELLOW,
    color: "#1A1A30", border: `1px solid ${YELLOW}`,
    fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
    textTransform: "lowercase", cursor: busy ? "wait" : "pointer",
    minHeight: 44,
  };
}
